import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { documentLibraryService } from "@/features/documents/services/documentLibraryService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  CreateDocumentLibraryRequest,
  DocumentLibrary,
  ListDocumentLibrariesQuery,
  UpdateDocumentLibraryRequest,
} from "@/types/document";

export function useDocumentLibraries(
  filters?: ListDocumentLibrariesQuery,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.documentLibraries.list(filters),
    queryFn: () => documentLibraryService.list(filters),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateDocumentLibrary() {
  const queryClient = useQueryClient();
  return useMutation<DocumentLibrary, Error, CreateDocumentLibraryRequest>({
    mutationFn: (payload) => documentLibraryService.create(payload),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentLibraries.all,
      });
      toastMutationSuccess(res, { fallbackMessage: "تم إنشاء المكتبة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useUpdateDocumentLibrary() {
  const queryClient = useQueryClient();
  return useMutation<
    DocumentLibrary,
    Error,
    { id: string; data: UpdateDocumentLibraryRequest }
  >({
    mutationFn: ({ id, data }) => documentLibraryService.update(id, data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentLibraries.all,
      });
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث المكتبة" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useDeleteDocumentLibrary() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => documentLibraryService.delete(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.documentLibraries.all,
      });
      toastMutationSuccess(res, { fallbackMessage: "تم حذف المكتبة" });
    },
    onError: (error) => handleApiError(error),
  });
}
